import type { RateCard, RateVersion } from "./types";

export class RateCardError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "RateCardError";
  }
}

/**
 * Resolves the epoch millisecond timestamp representing 00:00:00 on the date
 * specified by `dateStr` (e.g. "2026-10-01" or full ISO) within the given `timeZone`.
 */
export function getMidnightInTimezone(dateStr: string, timeZone: string): number {
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) {
    const directDate = new Date(dateStr);
    if (Number.isNaN(directDate.getTime())) {
      throw new RateCardError(`Invalid date string for rate version: "${dateStr}"`);
    }
    return directDate.getTime();
  }

  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const day = parseInt(match[3], 10);

  // Initial estimate using UTC midnight
  let guess = Date.UTC(year, month - 1, day, 0, 0, 0);

  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hourCycle: "h23",
  });

  // Iteratively refine using timezone offset
  for (let i = 0; i < 3; i++) {
    const parts = dtf.formatToParts(new Date(guess));
    let py = year,
      pm = month,
      pd = day,
      ph = 0,
      pmin = 0,
      ps = 0;

    for (const part of parts) {
      if (part.type === "year") py = parseInt(part.value, 10);
      else if (part.type === "month") pm = parseInt(part.value, 10);
      else if (part.type === "day") pd = parseInt(part.value, 10);
      else if (part.type === "hour") ph = parseInt(part.value, 10);
      else if (part.type === "minute") pmin = parseInt(part.value, 10);
      else if (part.type === "second") ps = parseInt(part.value, 10);
    }

    const currentLocalAsUtc = Date.UTC(py, pm - 1, pd, ph, pmin, ps);
    const targetLocalAsUtc = Date.UTC(year, month - 1, day, 0, 0, 0);
    const diff = targetLocalAsUtc - currentLocalAsUtc;
    if (diff === 0) break;
    guess += diff;
  }

  return guess;
}

/**
 * Returns the latest RateVersion whose effectiveFrom (interpreted at 00:00 in card.timezone)
 * is less than or equal to `at`.
 * Throws a typed `RateCardError` if no version matches.
 */
export function pickRateVersion(card: RateCard, at: Date): RateVersion {
  if (!card.versions || card.versions.length === 0) {
    throw new RateCardError("Rate card contains no versions.");
  }

  const atMs = at.getTime();
  let latestMatch: { version: RateVersion; effectiveTimestamp: number } | null = null;

  for (const version of card.versions) {
    const effectiveTimestamp = getMidnightInTimezone(version.effectiveFrom, card.timezone);
    if (effectiveTimestamp <= atMs) {
      if (!latestMatch || effectiveTimestamp > latestMatch.effectiveTimestamp) {
        latestMatch = { version, effectiveTimestamp };
      }
    }
  }

  if (!latestMatch) {
    throw new RateCardError(
      `No applicable rate version for date ${at.toISOString()} in timezone ${card.timezone}. Earliest version starts after this date.`,
    );
  }

  return latestMatch.version;
}
